import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Close as CloseIcon, Send as SendIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 500,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  content: {
    paddingTop: theme.spacing(2),
  },
  charCount: {
    textAlign: "right",
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  infoBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  phoneNumber: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
}));

const SendSmsModal = ({ open, onClose, contact, ticketId = null, onSuccess }) => {
  const classes = useStyles();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const maxLength = 160;
  const smsCount = Math.ceil(message.length / 153) || 1;
  const charsRemaining = message.length <= 160 
    ? 160 - message.length 
    : 153 - (message.length % 153);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Digite uma mensagem para enviar");
      return;
    }

    if (!contact?.number) {
      toast.error("Número de telefone inválido");
      return;
    }

    try {
      setLoading(true);

      await api.post("/sms", {
        phone: contact.number,
        message: message.trim(),
        contactId: contact.id,
        ticketId: ticketId
      });

      toast.success("SMS enviado com sucesso!");
      setMessage("");
      
      // Callback para atualizar saldo
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error("Erro ao enviar SMS:", error);
      toast.error(
        error.response?.data?.message || "Erro ao enviar SMS. Verifique as configurações."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setMessage("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.root }}
    >
      <DialogTitle>
        Enviar SMS
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.infoBox}>
          <Typography variant="body2" color="textSecondary">
            Enviando para:
          </Typography>
          <Typography variant="body1" className={classes.phoneNumber}>
            {contact?.name || "Sem nome"} - {contact?.number}
          </Typography>
        </Box>

        <TextField
          label="Mensagem"
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={loading}
          placeholder="Digite sua mensagem..."
          inputProps={{
            maxLength: 1000, // Limite técnico
          }}
        />

        <Typography className={classes.charCount}>
          {message.length} caracteres | {charsRemaining} restantes para o SMS {smsCount}
          {message.length > 160 && ` | ${smsCount} SMS serão enviados`}
        </Typography>

        {message.length > 160 && (
          <Typography variant="caption" color="textSecondary">
            ⚠️ Mensagens acima de 160 caracteres consomem múltiplos créditos (1 a cada 153 caracteres)
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={loading || !message.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {loading ? "Enviando..." : "Enviar SMS"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendSmsModal;
